export class ApiError extends Error {
  public constructor(
    message: string,
    public readonly status: number,
    public readonly details: unknown = null,
    public readonly requiredEntitlement: string | null = null,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

interface ApiEnvelope<T> {
  readonly data: T
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  readonly body?: unknown
  readonly timeoutMs?: number
  readonly authenticated?: boolean
}

type EntitlementRequiredHandler = (entitlement: string) => void

let entitlementRequiredHandler: EntitlementRequiredHandler | null = null

export function setEntitlementRequiredHandler(handler: EntitlementRequiredHandler | null): void {
  entitlementRequiredHandler = handler
}

const DEFAULT_TIMEOUT_MS = 12_000

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readErrorMessage(payload: unknown, fallback: string): string {
  if (!isRecord(payload)) return fallback
  for (const key of ['message', 'error', 'messages']) {
    const value = payload[key]
    if (typeof value === 'string' && value.trim().length > 0) return value
    if (isRecord(value)) {
      const first = Object.values(value).find((item) => typeof item === 'string')
      if (typeof first === 'string' && first.trim().length > 0) return first
    }
  }
  return fallback
}

export class HttpClient {
  public constructor(
    private readonly baseUrl: string,
    private readonly tokenProvider: () => string | null = () => null,
  ) { }

  public get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'GET' })
  }

  public post<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body })
  }

  public patch<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'PATCH', body })
  }

  public delete<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: 'DELETE', body })
  }

  private async request<T>(path: string, options: RequestOptions): Promise<T> {
    const controller = new AbortController()
    const timeout = globalThis.setTimeout(() => controller.abort(), options.timeoutMs ?? DEFAULT_TIMEOUT_MS)

    try {
      const { body, timeoutMs: _timeoutMs, authenticated = true, ...requestInit } = options
      void _timeoutMs
      const token = authenticated ? this.tokenProvider() : null
      const fetchInit: RequestInit = {
        ...requestInit,
        headers: {
          Accept: 'application/json',
          ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
          ...(token === null ? {} : { Authorization: `Bearer ${token}` }),
          ...requestInit.headers,
        },
        signal: controller.signal,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      }
      const response = await fetch(`${this.baseUrl}${path}`, fetchInit)

      const contentType = response.headers.get('content-type') ?? ''
      const payload: unknown = contentType.includes('application/json') ? await response.json() : await response.text()

      if (!response.ok) {
        const requiredEntitlement = response.headers.get('x-entitlement-required')?.trim() || null
        if (response.status === 403 && requiredEntitlement !== null) entitlementRequiredHandler?.(requiredEntitlement)
        throw new ApiError(
          readErrorMessage(payload, `Permintaan gagal dengan status ${response.status}.`),
          response.status,
          payload,
          requiredEntitlement,
        )
      }

      if (!isRecord(payload) || !('data' in payload)) {
        throw new ApiError('Format respons API tidak sesuai kontrak.', response.status, payload)
      }

      return (payload as unknown as ApiEnvelope<T>).data
    } catch (error: unknown) {
      if (error instanceof ApiError) throw error
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiError('Server terlalu lama merespons.', 408)
      }
      throw new ApiError(error instanceof Error ? error.message : 'Tidak dapat terhubung ke server.', 0, error)
    } finally {
      globalThis.clearTimeout(timeout)
    }
  }
}
